import { useTranslation } from 'react-i18next';
import { useLoaderData } from '@tanstack/react-router';
import { Landmark } from 'lucide-react';

import staticData from '@/data/contacts.json';
import Section from '@/components/layout/section.tsx';

const { VITE_LOCALE, VITE_MAP_API_KEY } = import.meta.env;

export default function Contacts() {
	const { t } = useTranslation();
	const data = useLoaderData({ from: '__root__' });

	return (
		<Section className="flex flex-col gap-8 max-md:gap-6">
			<div className="flex flex-col gap-2 max-lg:gap-1">
				<h2 className="text-[var(--primary)] font-bold text-5xl max-lg:text-4xl max-md:text-3xl text-center">
					{t('contacts.title')}
				</h2>

				<h3 className="text-neutral-600 font-bold text-5xl max-lg:text-4xl max-md:text-3xl text-center">
					{data?.contactFullAddress}
				</h3>
			</div>

			<div className="w-full h-[400px] relative">
				<iframe
					width="100%"
					height="400px"
					className="rounded-xl overflow-hidden"
					src={`https://www.google.com/maps?q=${data?.contactFullAddress}&hl=${VITE_LOCALE}&mode=place&output=embed&key=${VITE_MAP_API_KEY}`}
				/>
			</div>

			<div className="flex items-start justify-between gap-8 max-md:flex-wrap max-md:gap-4">
				<div className="flex flex-col gap-8 max-xl:gap-4">
					<h2 className="text-neutral-600 font-bold text-5xl max-xl:text-4xl max-lg:text-3xl text-left max-w-[500px] max-md:max-w-full">
						{t('contacts.contacts_title')}
					</h2>

					<div className="flex flex-col gap-2 max-xl:gap-2 text-neutral-600">
						{data?.contactEmail && (
							<div className="flex gap-4 items-center justify-start">
								<div className="w-[30px] h-[30px] max-xl:w-[24px] max-xl:h-[24px] relative">
									<img src="/icons/mail-icon.svg" alt="Email" />
								</div>
								<a
									className="hover:text-blue-700 hover:underline text-lg max-lg:text-[16px]"
									href={`mailto:${data?.contactEmail}`}
								>
									{data?.contactEmail}
								</a>
							</div>
						)}

						{data?.contactFullAddress && (
							<div className="flex gap-4 items-center justify-start">
								<div className="w-[30px] h-[30px] max-xl:w-[24px] max-xl:h-[24px] relative">
									<img src="/icons/location-icon.svg" alt="Email" />
								</div>
								<p className="text-lg max-lg:text-[16px]">{data?.contactFullAddress}</p>
							</div>
						)}

						{data?.nip && (
							<div className="flex gap-4 items-center justify-start">
								<div className="w-[30px] h-[30px] max-xl:w-[24px] max-xl:h-[24px] relative">
									<Landmark className="text-[var(--primary)] w-[30px] h-[30px] max-xl:w-[24px] max-xl:h-[24px]" />
								</div>

								<p className="text-lg max-lg:text-[16px]">NIP: {data?.nip}</p>
							</div>
						)}

						{data?.contactPhone && (
							<div className="flex gap-4 items-center justify-start">
								<div className="w-[30px] h-[30px] max-xl:w-[24px] max-xl:h-[24px] relative">
									<img src="/icons/phone-icon.svg" alt="Email" />
								</div>

								<span>
									<a
										href={`tel:${data?.contactPhone}`}
										className="hover:text-blue-700 hover:underline text-lg max-lg:text-[16px]"
									>
										{data?.contactPhone}
									</a>
								</span>
							</div>
						)}
					</div>
				</div>

				<div className="flex flex-col gap-6 max-xl:gap-4">
					<h2 className="text-[var(--primary)] font-bold text-5xl max-xl:text-4xl max-lg:text-3xl text-right max-md:text-left max-w-[600px] max-md:max-w-full">
						{t('contacts.socials_title')}
					</h2>

					<div className="flex items-center gap-4 max-md:gap-2 justify-end max-md:justify-start">
						{staticData?.social?.map((item, i) => (
							<a
								key={`soc_link_${i}`}
								href={item.href}
								target="_blank"
								className="w-[56px] h-[56px] max-lg:w-[42px] max-lg:h-[42px] flex items-center justify-center rounded-xl border border-[var(--light)] hover:border-[var(--primary)] transition"
							>
								<div className="w-[32px] h-[32px] max-lg:w-[24px] max-lg:h-[24px] relative">
									<img src={item.icon} alt={item.alt} />
								</div>
							</a>
						))}
					</div>

					<div className="text-lg max-lg:text-[16px] text-right max-md:text-left text-neutral-600 whitespace-break-spaces">
						{data?.workTime?.split('|')?.join('\n')}
					</div>
				</div>
			</div>
		</Section>
	);
}
